// Multi-recipient picker — checkbox list of contacts linked to the client /
// supplier, plus an "add another address" input. Used by quote + PO send dialogs.
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, UserPlus } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const RecipientPicker = ({ options, selected, onChange, onAddCustom, testPrefix = "recipient" }) => {
  const [custom, setCustom] = useState("");

  const toggle = (email) => {
    onChange(selected.includes(email) ? selected.filter((e) => e !== email) : [...selected, email]);
  };

  const addCustom = () => {
    const e = custom.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) return;
    if (!options.some((o) => o.email === e)) onAddCustom({ name: e, email: e, role: "Added manually" });
    if (!selected.includes(e)) onChange([...selected, e]);
    setCustom("");
  };

  return (
    <div className="space-y-1.5" data-testid={`${testPrefix}-picker`}>
      <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-40 overflow-y-auto">
        {options.length === 0 && (
          <div className="px-3 py-2 text-[11px] text-slate-400" data-testid={`${testPrefix}-empty`}>
            No linked contacts with an email — add an address below.
          </div>
        )}
        {options.map((o) => (
          <label key={o.email}
            className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-slate-50 text-xs"
            data-testid={`${testPrefix}-option-${o.email}`}>
            <Checkbox checked={selected.includes(o.email)} onCheckedChange={() => toggle(o.email)}
              data-testid={`${testPrefix}-check-${o.email}`} />
            <span className="font-medium text-slate-900 truncate">{o.name}</span>
            {o.role && (
              <Badge className="rounded-sm font-normal text-[9px] bg-slate-50 text-slate-500 border border-slate-200 shrink-0">
                {o.role}
              </Badge>
            )}
            <span className="text-slate-500 truncate ml-auto">{o.email}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <UserPlus size={13} className="text-slate-400 shrink-0" />
        <Input value={custom} onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          placeholder="Add another email address…" type="email"
          className="rounded-sm h-8 text-xs" data-testid={`${testPrefix}-custom-input`} />
        <Button type="button" size="sm" variant="outline" onClick={addCustom}
          disabled={!EMAIL_RE.test(custom.trim())}
          className="rounded-sm h-8 text-[11px]" data-testid={`${testPrefix}-custom-add-btn`}>
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
      <div className="text-[10px] text-slate-400">
        {selected.length === 0 ? "Select at least one recipient" : `Sending to ${selected.length} recipient${selected.length === 1 ? "" : "s"}`}
      </div>
    </div>
  );
};
